// Sign-up bonus: home page promo, register note and the customer's progress card
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { guessCountry } from "@/lib/giftStore";
import { Gift, Lock, CheckCircle2, ArrowRight } from "lucide-react";

const SYM = { USD: "$", CAD: "CA$" };
const fmt = (cur, v) => `${SYM[cur] || "$"}${Number(v || 0) % 1 ? Number(v).toFixed(2) : Number(v || 0).toFixed(0)}`;

function usePublicBonus() {
    const [b, setB] = useState(null);
    useEffect(() => { api.get("/public/signup-bonus").then((r) => setB(r.data)).catch(() => setB(null)); }, []);
    if (!b?.enabled) return null;
    const c = guessCountry();
    const a = b.amounts[c] || b.amounts.other;
    return { ...a, threshold: b.threshold, thresholdCur: a.currency, days: b.expire_days };
}

// Big promo for the home page hero
export function SignupBonusPromo({ className = "" }) {
    const b = usePublicBonus();
    if (!b) return null;
    return (
        <Link to="/register" data-testid="signup-bonus-promo"
            className={`group flex items-center gap-4 rounded-2xl bg-white border-2 border-brand/30 hover:border-brand px-4 py-3.5 shadow-sm transition-colors max-w-md ${className}`}>
            <span className="w-12 h-12 shrink-0 rounded-xl bg-brand text-white flex items-center justify-center"><Gift size={22} /></span>
            <span className="min-w-0 flex-1">
                <span className="block font-heading font-bold text-slate-900 text-lg leading-tight">Get {fmt(b.currency, b.amount)} free when you sign up</span>
                <span className="block text-xs text-slate-500 mt-0.5">Goes into your wallet once you've sold {fmt(b.thresholdCur, b.threshold)} in gift cards with us{b.days ? ` within ${b.days} days` : ""}.</span>
            </span>
            <ArrowRight size={18} className="text-brand shrink-0 transition-transform group-hover:translate-x-0.5" />
        </Link>
    );
}

// One line under the Sign up heading
export function SignupBonusNote() {
    const b = usePublicBonus();
    if (!b) return null;
    return (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-900 flex gap-2" data-testid="register-bonus-note">
            <Gift size={18} className="shrink-0 mt-0.5 text-emerald-700" />
            <span><b>{fmt(b.currency, b.amount)} welcome bonus</b> for new accounts. It's added to your wallet after you sell {fmt(b.thresholdCur, b.threshold)} in gift cards{b.days ? ` within ${b.days} days of signing up` : ""}.</span>
        </div>
    );
}

// Progress card for the customer dashboard and wallet
export function SignupBonusCard() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/bonus/me").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
    if (!d) return null;
    if (d.needs === "verify_email") {
        return (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3 text-sm text-emerald-900">
                <Gift size={20} className="shrink-0 text-emerald-700" /> Verify your email to claim your welcome bonus.
            </div>
        );
    }
    const b = d.bonus;
    if (!b || b.status === "blocked" || b.status === "expired") return null;
    const cur = b.currency;
    if (b.status === "paid") {
        return (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3 text-sm text-emerald-900" data-testid="bonus-card">
                <CheckCircle2 size={20} className="shrink-0 text-emerald-700" /> Your {fmt(cur, b.amount)} welcome bonus is in your wallet.
            </div>
        );
    }
    const exp = b.expires_at ? new Date(b.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
    return (
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="bonus-card">
            <div className="flex items-start gap-4">
                <span className="w-12 h-12 shrink-0 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Gift size={22} /></span>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading font-bold text-slate-900 text-lg">{fmt(cur, b.amount)} welcome bonus</span>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5"><Lock size={11} /> Locked</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">Sell {fmt(cur, b.left)} more in gift cards and it goes straight into your wallet{exp ? ` (by ${exp})` : ""}.</p>
                    <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={b.progress} aria-valuemin={0} aria-valuemax={100} aria-label="Bonus progress">
                        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.max(3, b.progress)}%` }} />
                    </div>
                    <div className="mt-1.5 flex justify-between text-xs text-slate-500"><span>{fmt(cur, b.sold)} sold</span><span>Goal {fmt(cur, b.threshold)}</span></div>
                </div>
            </div>
            <Link to="/dashboard/send" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800">Sell a gift card <ArrowRight size={15} /></Link>
        </div>
    );
}
